<template>
  <div class="view-contents">
    <div class="section-header">
      <div class="row bold-title">
        <div class="col">
          Plan Contacts
        </div>
      </div>
    </div>
    <LoadingSpinner v-if='formLoading' message ='Loading Project' size = '1x' />
    <div v-else-if="!projectSites.length">
      You have no contacts associated with this project.
    </div>
    <div v-else>
      <div class="row">
        <div v-for="site in projectSites" :key="site.relatedSiteId" class="col-9">
          <div class="site-section-wrapper">
            <div class="card">
              <div class="card-header">
                <a v-b-toggle="`site-${site.relatedSiteId}-card`" class="c-pointer">
                  <b-icon icon='caret-right-fill' class="when-closed" />
                  <b-icon icon='caret-down-fill' class="when-opened" />
                </a>
                <span class="details-label">Site: </span>
                <router-link
                  :to="{ name: 'Site', params: { id: site.relatedSiteId } }"
                  class="font-weight-bold">
                  {{ site.relatedSite.siteName }}
                </router-link>
              </div>
              <b-collapse :id="`site-${site.relatedSiteId}-card`" visible>
                <div class="card-body">
                  <div class="bold-title">Organizations</div>
                  <LoadingSpinner v-if='siteOrganizationsLoading.includes(site.relatedSiteId)'
                    message ='Loading Site Organizations' size = '1x'>
                  </LoadingSpinner>
                  <template v-else>
                    <div v-if="!organizations(site.relatedSiteId)
                      || organizations(site.relatedSiteId).length === 0">
                      No Organizations for this Site
                    </div>
                    <template v-else>
                      <div v-for="organization in organizations(site.relatedSiteId)"
                        :key="organization.id">
                        <hr/>
                        <div class="row">
                          <div class="col">
                            <a class="font-weight-bold" target="_blank"
                              :href="formatOrganizationUrl(organization.ORGANISATION_ID)">
                              {{ organization.ORGANISATION_NAME }} -
                            </a>
                            {{ organization.Role__c || 'N/A' }}
                            <template v-if="organization.Primary__c"> (Primary)</template>
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                  <hr/>
                  <div class="bold-title">Contacts</div>
                  <LoadingSpinner v-if='siteContactsLoading.includes(site.relatedSiteId)'
                    message ='Loading Site Contacts' size = '1x'>
                  </LoadingSpinner>
                  <template v-else>
                    <div v-if="!contacts(site.relatedSiteId)
                       || contacts(site.relatedSiteId).length === 0">
                      No Contacts for this Site
                    </div>
                    <template v-else>
                      <div v-for="contact in contacts(site.relatedSiteId)" :key="contact.id">
                        <hr/>
                        <div class="row">
                          <div class="col">
                            <a class="font-weight-bold" target="_blank"
                               :href="`${config.insightly.contactUrl}${contact.Contact__c}`">
                              {{ contact.FIRST_NAME }} {{ contact.LAST_NAME }} -
                              {{ contact.Role__c || 'N/A' }}
                              <template v-if="contact.Primary__c"> (Primary)</template>
                            </a>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col">
                            <span class="details-label">Email: </span>
                            <a v-if="contact.EMAIL_ADDRESS"
                              :href="`mailto:${contact.EMAIL_ADDRESS}?bcc=${getTaskEmail()}`">
                              {{contact.EMAIL_ADDRESS}}
                            </a>
                            <template v-else>N/A</template>
                            | <span class="details-label">Home: </span>
                            {{ contact.PHONE_HOME || 'N/A' }}
                            | <span class="details-label">Mobile: </span>
                            {{ contact.PHONE_MOBILE || 'N/A' }}
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

export default {
  name: 'ProjectContacts',
  components: {
    LoadingSpinner,
  },
  computed: {
    ...mapState(['currentSite', 'config']),
    ...mapState('User', {
      insightlyUser: (state) => state.insightlyUser,
    }),
    ...mapState('Project', {
      project: (state) => state.project,
      projectLoading: (state) => state.projectLoading,
      projectSites: (state) => state.projectSites,
      siteContactsLoading: (state) => state.siteContactsLoading,
      projectContacts: (state) => state.projectContacts,
      siteOrganizationsLoading: (state) => state.siteOrganizationsLoading,
      projectOrganizations: (state) => state.projectOrganizations,
    }),
  },
  data() {
    return {
      formLoading: false,
    };
  },
  mounted() {
    if (this.project != null) {
      this.load();
    } else {
      const unsubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type === 'Project/SET_PROJECT') {
          unsubscribe();
          this.load();
        }
      });
    }
  },
  methods: {
    async load() {
      this.formLoading = true;
      await this.$store.dispatch('Project/getProject', parseInt(this.$route.params.id, 10));
      await this.$store.dispatch('Project/getProjectSites', this.project.id);
      await Promise.all(this.projectSites.flatMap((ps) => [
        this.$store.dispatch('Project/getProjectContacts', ps.relatedSiteId),
        this.$store.dispatch('Project/getProjectOrganizations', ps.relatedSiteId),
      ]));
      this.formLoading = false;
    },
    organizations(siteId) {
      let contacts = [];
      const organizationFilter = this.projectOrganizations
        .filter((organizationsObj) => organizationsObj.siteId === siteId);
      if (organizationFilter.length > 0) {
        contacts = organizationFilter[0].organizations
          .slice()
          .sort((a, b) => {
            // sort by Primary first, then by first name, then by last name
            if (a.Primary__c && !b.Primary__c) {
              return -1;
            }
            if (!a.Primary__c && b.Primary__c) {
              return 1;
            }
            return a.ORGANISATION_NAME.localeCompare(b.ORGANISATION_NAME);
          });
      }
      return contacts;
    },
    contacts(siteId) {
      let contacts = [];

      const contactFilter = this.projectContacts
        .filter((contactsObj) => contactsObj.siteId === siteId);

      if (contactFilter.length > 0) {
        contacts = contactFilter[0].contacts
          .slice()
          .sort((a, b) => {
            // sort by Primary first, then by first name, then by last name
            if (a.Primary__c && !b.Primary__c) {
              return -1;
            }
            if (!a.Primary__c && b.Primary__c) {
              return 1;
            }
            const firstNameCompare = a.FIRST_NAME.localeCompare(b.FIRST_NAME);
            if (firstNameCompare !== 0) {
              return firstNameCompare;
            }
            return a.LAST_NAME.localeCompare(b.LAST_NAME);
          });
      }
      return contacts;
    },
    formatOrganizationUrl(orgId) {
      return `${this.config.insightly.organizationUrl}${orgId}`;
    },
    getTaskEmail() {
      const idIndex = this.insightlyUser.emaiL_DROPBOX_ADDRESS
        .indexOf(this.insightlyUser.emaiL_DROPBOX_IDENTIFIER);
      const originalEmail = this.insightlyUser.emaiL_DROPBOX_ADDRESS;
      return `${originalEmail.substr(0, idIndex)}P${this.project.id}-${originalEmail.substr(idIndex, originalEmail.length - 1)}`;
    },
  },
};
</script>
